<script>
import useTools from "@/use/Tools";
import useSharedData from "@/use/SharedData";
import {onMounted, ref} from "vue";
import useSharedMethod from "@/use/SharedMethod";
import StrWorker from "@/components/StrWorker.vue";
import {usePaySlipStore} from "@/stores/PaySlip";
import axios from "axios";

export default {
    name: "CorrectionForm",
    components: {StrWorker},
    setup() {
        const {$route} = useTools();
        const {dialog, loading} = useSharedData();
        const {getBack, translateMonth, transformFilterValues} = useSharedMethod();
        
        const data = ref({
            workers: [],
            filter: {},
        });
        const createNewPaySlip = ref(false);
        const onlyThisWorker = ref(true);
        const tagId = ref(null);
        
        const selectedAnnexes = ref([]);
        const annex = ref({});
        const paySlip = ref({});
        
        function findAnnexById() {
            loading.value = true;
            axios.get(`/paySlipAnnexes/${$route.params.annexId}`).then(response => {
                annex.value = response.data;
                findAllAnnexes();
            });
        }
        
        function findPaySlipById() {
            axios.get(`/payments/${$route.params.id}`).then(response => {
                paySlip.value = response.data;
            });
        }
        
        const annexes = ref([]);
        
        function findAllAnnexes() {
            axios.get(`/paySlipAnnexes`, {
                params: transformFilterValues({
                    sortBy: 'month',
                    descending: true,
                    allRecords: true
                })
            }).then(response => {
                annexes.value = response.data.elements;
            }).finally(() => {
                loading.value = false;
            })
        }
        
        
        const annexDialog = ref(false);
        
        
        function addAnnex(annex) {
            selectedAnnexes.value.push(annex);
        }
        
        function removeAnnex(id) {
            selectedAnnexes.value = selectedAnnexes.value.filter(obj => obj.id !== id);
        }
        
        function select(paySlip) {
            if (isSelected(paySlip)) {
                removeAnnex(paySlip.id);
            } else {
                addAnnex(paySlip);
            }
        }
        
        function isSelected(annex) {
            return selectedAnnexes.value.some(obj => obj.id === annex.id);
        }
        
        const paySectionSelectDialog = ref(false);
        const selectedPaySections = ref([]);
        
        const correctionValues = ref([]);
        
        function addPaySections() {
            paySectionSelectDialog.value = false;
            selectedPaySections.value.forEach((element) => {
                let index = correctionValues.value.findIndex(obj => obj.paySectionId === element.id);
                if (index === -1)
                    correctionValues.value.push({
                        paySectionId: element.id,
                        paySectionCode: element.code,
                        paySectionLabel: element.label,
                        value: 0,
                        byPercent: false
                    });
            });
            selectedPaySections.value = [];
            
        }
        
        function closePaySectionDialog() {
            paySectionSelectDialog.value = false;
        }
        
        function removeValue(paySectionId) {
            correctionValues.value = correctionValues.value.filter(obj => obj.paySectionId !== paySectionId);
        }
        
        const paySlipStore = usePaySlipStore();
        
        function calculate() {
            loading.value = true;
            const body = {
                ids: onlyThisWorker.value ? [paySlip.value.workerId] : data.value.workers.map((w) => w.id),
                annexIds: selectedAnnexes.value.map((a) => a.id),
                annexId: $route.params.annexId,
                tagId: tagId.value,
                createNewPaySlip: createNewPaySlip.value,
                correctionValues: correctionValues.value,
            };
            axios.post(`/corrections/calculate`, body, {
                params: transformFilterValues(data.value.filter),
            }).then(response => {
                if (createNewPaySlip.value) {
                    paySlipStore.addAll(response.data);
                } else {
                    paySlipStore.updateAll(response.data);
                }
                getBack();
            }).finally(() => {
                loading.value = false;
            })
        }
        
        onMounted(() => {
            findAnnexById();
            findPaySlipById();
        });
        
        return {
            annexDialog,
            loading,
            dialog,
            createNewPaySlip,
            onlyThisWorker,
            tagId,
            paySectionSelectDialog,
            selectedPaySections,
            selectedAnnexes,
            correctionValues,
            data,
            annex,
            paySlip,
            annexes,
            getBack,
            translateMonth,
            select,
            isSelected,
            removeAnnex,
            addPaySections,
            closePaySectionDialog,
            removeValue,
            calculate
        }
    }
}
</script>

<template>
    <div>
        <v-dialog
            v-model="annexDialog"
            max-width="400"
            scrollable
        >
            <v-card>
                <w-toolbar color="teal" :title="$t('worker.ats.pay_reference')"/>
                <v-card-text>
                    <v-list dense>
                        <template v-for="(item, index) in annexes">
                            <v-list-item
                                :key="`m-${index}`"
                                @click="select(item)"
                            >
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-action>
                                    <v-icon
                                        v-if="isSelected(item)"
                                        color="teal"
                                    >
                                        mdi-check-circle
                                    </v-icon>
                                </v-list-item-action>
                            </v-list-item>
                            <v-divider v-if="index < annexes.length - 1" :key="`d-${index}`"/>
                        </template>
                    </v-list>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="annexDialog = false"/>
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <v-dialog
            v-model="dialog"
            persistent
            scrollable
            max-width="1300"
        >
            <v-card>
                <w-toolbar color="teal" :title="annex.name"/>
                <v-card-text class="ma-0">
                    <v-container fluid>
                        <str-worker v-model="data" action="UPDATE_PAY_SLIP">
                            <w-fieldset
                                :title="$t('pay_slip.pay_to_be_regularized')"
                                show-add-action
                                @add="annexDialog = true"
                            >
                                <v-simple-table dense fixed-header height="150">
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th class="text-left"> {{ $t('common.month') }}</th>
                                            <th class="text-left"> {{ $t('common.name') }}</th>
                                            <th class="text-left"></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr
                                            v-for="item in selectedAnnexes"
                                            :key="item.id"
                                        >
                                            <td class="text-left">{{ item.month }}</td>
                                            <td class="text-left">{{ item.name }}</td>
                                            <td class="text-left">
                                                <w-btn-icon
                                                    icon="mdi-trash-can"
                                                    color="red"
                                                    @press="removeAnnex(item.id)"
                                                />
                                            </td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </w-fieldset>
                            <br/>
                            <w-fieldset
                                :title="$t('pay_slip.value_to_be_corrected')"
                                show-add-action
                                @add="paySectionSelectDialog = true"
                            >
                                <v-simple-table dense fixed-header height="130">
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th class="text-left"> {{ $t('common.code') }}</th>
                                            <th class="text-left"> {{ $t('common.label') }}</th>
                                            <th class="text-left"> {{ $t('common.value') }}</th>
                                            <th class="text-left"> {{ $t('common.percent') }}</th>
                                            <th class="text-left"></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr
                                            v-for="item in correctionValues"
                                            :key="item.paySectionId"
                                        >
                                            <td class="text-left">{{ item.paySectionCode }}</td>
                                            <td class="text-left">{{ item.paySectionLabel }}</td>
                                            <td class="text-left">
                                                <w-edit-dialog
                                                    v-model="item.value"
                                                    price-format
                                                    hide-details
                                                    hide-chip
                                                />
                                            </td>
                                            <td class="text-left">
                                                <v-simple-checkbox v-model="item.byPercent"/>
                                            </td>
                                            <td class="text-left">
                                                <w-btn-icon
                                                    icon="mdi-trash-can"
                                                    color="red"
                                                    @press="removeValue(item.paySectionId)"
                                                />
                                            </td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </w-fieldset>
                            <br/>
                            <v-row dense>
                                <v-col cols="6">
                                    <w-fieldset :title="$t('pay_slip.correction_extended')">
                                        <v-radio-group hide-details v-model="onlyThisWorker">
                                            <v-radio
                                                :value="true"
                                                class="mt-n3"
                                                :label="`${$t('pay_slip.correction_this_worker')} (${paySlip.workerCode}-${paySlip.workerName})`"
                                            />
                                            <v-radio
                                                :value="false"
                                                :label="$t('pay_slip.correction_all_workers')"
                                            />
                                        </v-radio-group>
                                    </w-fieldset>
                                </v-col>
                                <v-col cols="6">
                                    <w-fieldset :title="$t('pay_slip.correction_pay_result')">
                                        <v-row dense>
                                            <v-col cols="12">
                                                <v-radio-group hide-details v-model="createNewPaySlip">
                                                    <v-radio
                                                        :value="false"
                                                        class="mt-n3"
                                                        :label="`${$t('pay_slip.correction_add_to_pay')} ${paySlip.annexName}`"
                                                    />
                                                    <v-radio
                                                        :value="true"
                                                        :label="$t('pay_slip.correction_create_new_pay')"
                                                    />
                                                </v-radio-group>
                                            </v-col>
                                            <v-col cols="12" v-if="createNewPaySlip">
                                                <w-data-select-autocomplete
                                                    v-model="tagId"
                                                    show-default-label
                                                    type="PAY_SLIP_TAG"
                                                />
                                            </v-col>
                                        </v-row>
                                    </w-fieldset>
                                </v-col>
                            </v-row>
                        </str-worker>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <w-btn-close @press="getBack"/>
                    <w-btn-add
                        show
                        :loading="loading"
                        @press="calculate"
                    />
                </v-card-actions>
            </v-card>
        </v-dialog>
        
        <select-pay-section-component
            :dialog="paySectionSelectDialog"
            :selected="selectedPaySections"
            disable-suggestion
            @save="addPaySections"
            @close="closePaySectionDialog"
        />
    </div>
</template>

<style scoped>

</style>